import React, { useState, useEffect } from 'react';
import { FaTimes, FaSave } from 'react-icons/fa';
import api from '../../../services/api';

const TaskFormModal = ({ task, statuses, priorities, tags, users, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status_id: '',
    priority_id: '',
    assigned_to: '',
    due_date: '',
    estimated_hours: '',
    category: '',
    tags: []
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        status_id: task.status_id || '',
        priority_id: task.priority_id || '',
        assigned_to: task.assigned_to || '',
        due_date: task.due_date ? new Date(task.due_date).toISOString().slice(0, 16) : '',
        estimated_hours: task.estimated_hours || '',
        category: task.category || '',
        tags: task.tags ? task.tags.map(t => t.id) : []
      });
    } else {
      // Set defaults for new task
      setFormData(prev => ({
        ...prev,
        status_id: statuses.find(s => s.is_default)?.id || statuses[0]?.id || '',
        priority_id: priorities.find(p => p.is_default)?.id || priorities[1]?.id || ''
      }));
    }
  }, [task, statuses, priorities]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        due_date: formData.due_date || null,
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
        assigned_to: formData.assigned_to || null
      };

      if (task) {
        await api.put(`/portal/tasks/${task.id}`, submitData);
      } else {
        await api.post('/portal/tasks', submitData);
      }

      onSave();
    } catch (error) {
      console.error('Error saving task:', error);
      setError(error.response?.data?.message || 'Failed to save task');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTagToggle = (tagId) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tagId)
        ? prev.tags.filter(id => id !== tagId)
        : [...prev.tags, tagId]
    }));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container task-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{task ? 'Edit Task' : 'Create New Task'}</h2>
          <button className="btn-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}

          {/* Title */}
          <div className="form-group">
            <label className="form-label required">Task Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="form-input"
              required
              placeholder="Enter task title..."
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="form-textarea"
              rows="4"
              placeholder="Enter task description..."
            />
          </div>

          {/* Two Column Layout */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label required">Status</label>
              <select
                value={formData.status_id}
                onChange={(e) => handleChange('status_id', e.target.value)}
                className="form-select"
                required
              >
                <option value="">Select Status</option>
                {statuses.map(status => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label required">Priority</label>
              <select
                value={formData.priority_id}
                onChange={(e) => handleChange('priority_id', e.target.value)}
                className="form-select"
                required
              >
                <option value="">Select Priority</option>
                {priorities.map(priority => (
                  <option key={priority.id} value={priority.id}>
                    {priority.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Assigned To */}
          <div className="form-group">
            <label className="form-label">Assign To</label>
            <select
              value={formData.assigned_to}
              onChange={(e) => handleChange('assigned_to', e.target.value)}
              className="form-select"
            >
              <option value="">Unassigned</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.first_name && user.last_name
                    ? `${user.first_name} ${user.last_name} (${user.email})`
                    : user.email}
                </option>
              ))}
            </select>
          </div>

          {/* Due Date and Estimated Hours */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input
                type="datetime-local"
                value={formData.due_date}
                onChange={(e) => handleChange('due_date', e.target.value)}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Estimated Hours</label>
              <input
                type="number"
                step="0.5"
                min="0"
                value={formData.estimated_hours}
                onChange={(e) => handleChange('estimated_hours', e.target.value)}
                className="form-input"
                placeholder="0"
              />
            </div>
          </div>

          {/* Category */}
          <div className="form-group">
            <label className="form-label">Category</label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className="form-input"
              placeholder="e.g., Development, Design, Testing..."
            />
          </div>

          {/* Tags */}
          {tags && tags.length > 0 && (
            <div className="form-group">
              <label className="form-label">Tags</label>
              <div className="tag-selector">
                {tags.map(tag => (
                  <button
                    key={tag.id}
                    type="button"
                    className={`tag-chip ${formData.tags.includes(tag.id) ? 'selected' : ''}`}
                    style={{
                      backgroundColor: formData.tags.includes(tag.id) ? tag.color : tag.color + '20',
                      color: formData.tags.includes(tag.id) ? '#fff' : tag.color,
                      borderColor: tag.color
                    }}
                    onClick={() => handleTagToggle(tag.id)}
                  >
                    {tag.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="btn btn-primary">
              <FaSave /> {loading ? 'Saving...' : (task ? 'Update Task' : 'Create Task')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskFormModal;
